/**
 * Lenis scroll presets.
 * Desktop (>767px): original wheel smoothing only.
 * Mobile (≤767px): smooth syncTouch + capped release coast (story sections).
 */

export const MOBILE_MQ = "(max-width: 767px)";

export const DESKTOP_SCROLL_SLOWDOWN = 0.9;

export const EASE_LINEAR_OUT_EXPO = (t) =>
  Math.min(1, 1.001 - Math.pow(2, -10 * t));

/** Mobile — wheel / touch programmatic deceleration */
export const EASE_PREMIUM_OUT = (t) =>
  t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);

/** Mobile anchor jumps */
export const EASE_OUT_QUART = (t) => 1 - (1 - t) ** 4;

/** Mobile section snap — ease-in-out for even acceleration and soft landing. */
export const EASE_SNAP_SMOOTH = (t) => {
  const x = t >= 1 ? 1 : t <= 0 ? 0 : t;
  return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
};

/** Fast section snap — cubic ease-out for a quick, decisive slide into place. */
export const EASE_SNAP_FAST = (t) => {
  const x = t >= 1 ? 1 : t <= 0 ? 0 : t;
  return 1 - (1 - x) ** 3;
};

export function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function isCoarsePointer() {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return true;
  }
}

function scrollPerformanceHints() {
  let lowTier = false;
  try {
    const cores = navigator.hardwareConcurrency ?? 8;
    const memGB =
      navigator.deviceMemory != null ? navigator.deviceMemory : Infinity;
    lowTier = cores <= 4 || memGB <= 4;
  } catch {
    lowTier = false;
  }

  const shortSide =
    typeof window !== "undefined"
      ? Math.min(window.innerWidth, window.innerHeight)
      : 400;
  const sizeFactor = Math.min(Math.max(shortSide / 420, 0.9), 1.08);

  return { lowTier, sizeFactor };
}

/** Soft cap on touch/wheel spikes — bleeds excess instead of a hard cut (reduces jerk). */
export function capVirtualScrollDelta(maxAbs) {
  const bleed = 0.44;
  const cap = (v) => {
    if (v === 0) return 0;
    const abs = Math.abs(v);
    if (abs <= maxAbs) return v;
    const sign = Math.sign(v);
    return sign * (maxAbs + (abs - maxAbs) * bleed);
  };
  return (data) => {
    data.deltaX = cap(data.deltaX);
    data.deltaY = cap(data.deltaY);
    return true;
  };
}

/** Desktop — unchanged baseline (wheel only, no syncTouch). */
export function buildDesktopLenisOptions() {
  if (prefersReducedMotion()) {
    return {
      smoothWheel: false,
      wheelMultiplier: 1,
      touchMultiplier: 1,
      autoResize: true,
    };
  }

  const { lowTier, sizeFactor } = scrollPerformanceHints();

  return {
    duration: lowTier ? 0.92 : 1,
    easing: EASE_LINEAR_OUT_EXPO,
    smoothWheel: true,
    wheelMultiplier: 0.88 * sizeFactor * DESKTOP_SCROLL_SLOWDOWN,
    touchMultiplier: 1,
    lerp: lowTier ? 0.115 : 0.095,
    autoResize: true,
  };
}

/**
 * Mobile glide timeline — single source of truth (≤767px only).
 * Desktop uses buildDesktopLenisOptions() only.
 */
/** Trailing smoothness — lower = longer glide between frames. */
export const MOBILE_LERP = 0.058;
/** Lenis programmatic scroll (scrollTo / anchor). */
export const MOBILE_SCROLL_DURATION_SEC = 1.62;
/** Post-release coast — max ease-out length. */
export const MOBILE_SMOOTH_DURATION_SEC = 1.75;
export const MOBILE_COAST_MIN_DURATION_SEC = 0.58;
/** Thumb-lift momentum → scroll distance. */
export const MOBILE_RELEASE_DISTANCE_MULTIPLIER = 1.9;
/** Max coast travel as a fraction of viewport height. */
export const MOBILE_RELEASE_MAX_COAST_VH = 0.72;
export const MOBILE_COAST_SPEED_FACTOR = 0.27;
export const MOBILE_ANCHOR_SCROLL_DURATION_SEC = MOBILE_SCROLL_DURATION_SEC;
/** Story-section band snap — soft landing into the next step band. */
export const MOBILE_GATE_SNAP_DURATION_SEC = 0.32;
/** Section viewport-top snap — fast alignment, synced to gate snap. */
export const MOBILE_SECTION_SNAP_DURATION_SEC = 0.34;
export const MOBILE_SECTION_SNAP_MIN_DURATION_SEC = 0.2;
export const MOBILE_SECTION_SNAP_MAX_TRAVEL_VH = 0.36;
const MOBILE_WHEEL_MULTIPLIER = 1.42;
const MOBILE_TOUCH_MULTIPLIER = 2.4;
const MOBILE_SMOOTH_WHEEL = true;
const MOBILE_SYNC_TOUCH_LERP = 0.5;
const MOBILE_IOS_SYNC_TOUCH_LERP = 0.38;
const MOBILE_TOUCH_INERTIA_EXPONENT = 1.0;
const MOBILE_IOS_TOUCH_INERTIA_EXPONENT = 1.04;
const MOBILE_VIRTUAL_SCROLL_DELTA_CAP = 780;
const MOBILE_VIRTUAL_SCROLL_DELTA_CAP_LOW = 580;

function isIOSSafariUA() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return iOS && /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome\/\d+/i.test(ua);
}

export function buildMobileLenisOptions() {
  if (prefersReducedMotion()) {
    return {
      syncTouch: false,
      smoothWheel: false,
      wheelMultiplier: 1,
      touchMultiplier: 1,
      autoResize: true,
    };
  }

  const { lowTier } = scrollPerformanceHints();
  const iosSafari = isIOSSafariUA();
  const deltaCap = lowTier
    ? MOBILE_VIRTUAL_SCROLL_DELTA_CAP_LOW
    : MOBILE_VIRTUAL_SCROLL_DELTA_CAP;
  const syncTouchLerp = iosSafari
    ? MOBILE_IOS_SYNC_TOUCH_LERP
    : MOBILE_SYNC_TOUCH_LERP;

  return {
    lerp: MOBILE_LERP,
    duration: MOBILE_SCROLL_DURATION_SEC,
    wheelMultiplier: MOBILE_WHEEL_MULTIPLIER,
    touchMultiplier: MOBILE_TOUCH_MULTIPLIER,
    smoothWheel: MOBILE_SMOOTH_WHEEL,
    easing: EASE_PREMIUM_OUT,
    syncTouch: true,
    syncTouchLerp,
    touchInertiaExponent: iosSafari
      ? MOBILE_IOS_TOUCH_INERTIA_EXPONENT
      : MOBILE_TOUCH_INERTIA_EXPONENT,
    overscroll: false,
    autoResize: true,
    stopInertiaOnNavigate: true,
    virtualScroll: capVirtualScrollDelta(deltaCap),
  };
}

export function anchorScrollDuration(isMobile) {
  if (prefersReducedMotion()) return undefined;
  if (isMobile) return MOBILE_ANCHOR_SCROLL_DURATION_SEC;
  return 1.45 / DESKTOP_SCROLL_SLOWDOWN;
}

export function anchorScrollEasing(isMobile) {
  if (prefersReducedMotion()) return undefined;
  return isMobile ? EASE_PREMIUM_OUT : EASE_LINEAR_OUT_EXPO;
}
